import callCollectList from 'components/Dashboard/utilities/callCollectList';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import useGeneralRequestProperties from 'components/Dashboard/hooks/useGeneralRequestProperties';
import { useContext, useState } from 'react';
import { useSiteSectionsNames } from 'utils/getAppSite';
import { store } from 'components/Store';
import useNumberOfUnseenItems from 'components/Dashboard/hooks/useNumberOfUnseenItems';
import useGetSectionFilters from 'components/Dashboard/Filters/useGetSectionFilters';
import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import uppercaseFirstLetter from 'components/Dashboard/utilities/uppercaseFirstLetter';
import { changeTypeOfResource } from 'components/Dashboard/helpers/createTypeOfResource';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import useGetAccessToken from 'components/Dashboard/apiCalls/useGetAccessToken';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import useCreateParliamentLiveFilters from 'components/Dashboard/ParliamentLive/useCreateParliamentLiveFilters';
import {
  getActiveUpdateAtIndex,
  getFirstUnratedIndex,
  shouldUpdateActiveItem,
} from 'components/Dashboard/RateUpdatesFeed/rateUpdatesHelpers';

const useCreateResults = (props) => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { sectionsView, realtimeEvents } = state;
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { isAuthenticated } = useAuth0();
  const { getAccessToken } = useGetAccessToken();
  const [isLoading, setIsLoading] = useState(true);
  const [currentlyProcessing, setCurrentlyProcessing] = useState({ attempt: 0, state: true });
  const { selectedOptionParliamentLiveView } = sectionsView;

  const { numberOfUnseenItems } = useNumberOfUnseenItems();
  const { propsSectionName, customTitle, setActiveUpdate, activeUpdate } = props;

  const sectionName = propsSectionName ?? createSectionName();
  const { createRequestProperties, getLastSeenMarkers, updateMarkers } = useGeneralRequestProperties({ sectionName });
  const { generalApiCall } = useGeneralApiCall();
  const { addMetaTags } = useAddMetaTags();
  const { getName } = useSiteSectionsNames();
  const { createParliamentLiveFilters } = useCreateParliamentLiveFilters();
  const { initialCallSecondDateValue } = createParliamentLiveFilters({});

  const { search, keywordsLists } = state;
  const { list } = search;
  const sectionNameForFilters =
    sectionName === 'Influence' && location.pathname.includes('/lobbying-material')
      ? 'LobbyingMaterial'
      : sectionName === 'SocialMedia'
        ? 'Tweets'
        : sectionName;
  const { contentResults: contextResults, setContentResults } = useContentResults();
  const section = contextResults[sectionName] ?? {};

  const { getSectionFilters } = useGetSectionFilters(sectionNameForFilters);
  const sectionFilters = getSectionFilters();
  const { hash } = sectionFilters ?? {};
  const { id, page } = params;
  const idByUrl = typeof id !== 'undefined';
  const pageSize = sectionName === 'ElectionKeyUpdates' ? 100 : parseInt(import.meta.env.VITE_APP_PAGE_SIZE);
  const { mainSectionTitle, searchResultsSection, inboxSection } = useMainSectionParameter(params);

  async function createResults({
    pageNumber,
    setMarker,
    source,
    notForceLoading,
    requestProperties: requestPropertiesProp,
    fromInterval,
    localRealTimeEvents,
    returnOnlyForInterval,
    clearScrollPosition,
  }) {
    try {
      const directTopic =
        sectionName === 'Influence' && !inboxSection && !!getUrlParam('topic-id')
          ? keywordsLists.find((item) => item?.id === parseInt(getUrlParam('topic-id')))
          : null;
      const noUseQuery = sectionName === 'Influence' && location.pathname.startsWith('/campaigns');
      const { url, requestProperties, parliamentLiveReferenceDates } = createRequestProperties({
        pageNumber,
        directTopic,
        noUseQuery,
      });
      const currentTimeEvents = localRealTimeEvents ?? realtimeEvents;
      const notLoadingConditional =
        !pageNumber && (currentlyProcessing?.state || !currentlyProcessing) && !notForceLoading;
      let requestPropertiesToUse = requestPropertiesProp ?? requestProperties;

      // If `startDateTime` is present in URL for monitor-inbox style feeds, cap the initial call
      // to items before that timestamp so older items load first and new ones appear as badge updates.
      const startDateTimeParam = getUrlParam('startDateTime');
      if (
        inboxSection &&
        startDateTimeParam &&
        sectionName !== 'ParliamentLive' &&
        pageNumber == null &&
        !fromInterval &&
        !returnOnlyForInterval &&
        requestPropertiesToUse?.filters
      ) {
        const dateField = 'addedToFeedUtc';

        const newFilters = [
          ...(requestPropertiesToUse?.filters ?? []).filter((item) => item?.field !== dateField),
          // Without interval progression we only need an upper bound.
          // `startDateTime` is treated as the `addedToFeedUtc` exclusive cutoff.
          { field: dateField, value: `${initialCallSecondDateValue}Z`, operator: 'date_lt' },
        ];

        requestPropertiesToUse = { ...requestPropertiesToUse, filters: newFilters };
      }

      if (notLoadingConditional && !returnOnlyForInterval) {
        setIsLoading(true);
      }

      //GET MARKERS TO UNSEEN CONTENT
      if (
        inboxSection &&
        !!list &&
        notLoadingConditional &&
        list !== 'noListsCreated' &&
        setMarker &&
        !returnOnlyForInterval
      ) {
        await getLastSeenMarkers({ source });
      }

      let token = isAuthenticated ? await getAccessToken() : '';
      let results =
        list !== 'noListsCreated'
          ? await generalApiCall({
              pathname: url,
              method: !!requestProperties ? 'post' : 'get',
              requestProperties: requestPropertiesToUse,
              needsAuthentication: isAuthenticated && !!requestProperties,
              requestSource: source,
              notShowErrorMessage: true,
            })
          : {};

      if (results) {
        if (
          notLoadingConditional &&
          list !== 'noListsCreated' &&
          ((setMarker && results?.isReady) || searchResultsSection) &&
          !returnOnlyForInterval
        ) {
          numberOfUnseenItems(source);
        }

        if (results?.hits?.length > 0 && sectionName === 'CustomContacts' && !returnOnlyForInterval) {
          dispatch({
            type: 'MODIFY_SECTION',
            parameter: 'customContactsOnSearch',
            value: true,
          });
        }

        let hitsHighlighted =
          results?.highlights?.length > 0
            ? results?.hits.map((item, index) => {
                return {
                  ...item,
                  highlights: results?.highlights[index],
                };
              })
            : (results?.hits ?? []);

        const currentId = idByUrl ? parseInt(id) : results.hits?.length > 0 ? results.hits[0].id : 0;

        const buildStateResults = (prevContentResults) => {
          const prevSection = prevContentResults[sectionName] ?? {};
          let newsIds = pageNumber ? (prevSection.newsIds ?? []) : [];
          let hitsResults = pageNumber ? (prevSection.hits ?? []) : [];
          let explanations = pageNumber ? (prevSection.explanations ?? []) : [];
          let feedItemFlags = pageNumber ? (prevSection.feedItemFlags ?? []) : [];
          let collapsedHits = pageNumber ? (prevSection.collapsedHits ?? []) : [];

          if (sectionName === 'ParliamentLive') {
            hitsResults = [
              ...hitsHighlighted.map((item) => ({
                ...item,
                freshItem:
                  notForceLoading &&
                  selectedOptionParliamentLiveView === 'Relevant remarks' &&
                  prevSection?.hits?.length > 0,
              })),
              ...hitsResults,
            ];
            if (selectedOptionParliamentLiveView === 'Relevant remarks') {
              if (!searchResultsSection) {
                explanations = results?.explanations
                  ? [...results?.explanations, ...explanations]
                  : (prevSection?.explanations ?? []);
                feedItemFlags = results?.feedItemFlags
                  ? [...results?.feedItemFlags, ...feedItemFlags]
                  : (prevSection?.feedItemFlags ?? []);
                collapsedHits = results?.collapsedHits
                  ? [...results?.collapsedHits, ...collapsedHits]
                  : (prevSection?.collapsedHits ?? []);
              }
            } else {
              const liveEventIds = new Set(
                currentTimeEvents.filter((event) => event.eventHasEnded === false).map((event) => event.id)
              );
              const sortedHits = hitsHighlighted.sort((a, b) => {
                const aIsLive = liveEventIds.has(a.eventId);
                const bIsLive = liveEventIds.has(b.eventId);

                if (aIsLive === bIsLive) return 0;
                return aIsLive ? -1 : 1;
              });
              hitsResults = sortedHits;
              feedItemFlags = results?.feedItemFlags;
              explanations = results?.explanations;
              collapsedHits = results?.collapsedHits;
            }
          } else {
            hitsResults = hitsResults.concat(hitsHighlighted);
            explanations = explanations.concat(results?.explanations);
            feedItemFlags = results?.feedItemFlags
              ? feedItemFlags.concat(results?.feedItemFlags)
              : (prevSection?.feedItemFlags ?? []);
            newsIds = newsIds.concat(results?.hits?.map((item) => item.id));
            collapsedHits = collapsedHits?.concat(results?.collapsedHits ?? []);
          }
          return {
            ...prevContentResults,
            [sectionName]: {
              ...results,
              currentId,
              newsIds,
              explanations,
              feedItemFlags,
              collapsedHits,
              loaded: true,
              currentPositionId: newsIds.indexOf(currentId),
              hits: hitsResults,
              pageNumber: pageNumber ?? results.pageNumber ?? 1,
              thereIsMoreContent: results.hits?.length === pageSize,
              parliamentLiveReferenceDates,
              fromInterval,
              ...(clearScrollPosition ? { scrollPosition: null } : {}),
            },
          };
        };

        if (returnOnlyForInterval) {
          return buildStateResults(contextResults);
        }
        if (setActiveUpdate) {
          const hits = results?.hits ?? [];
          const feedItemFlags = results?.feedItemFlags ?? [];
          const rateUpdatesMode = getUrlParam('mode') === 'rate-updates';
          if (hits.length > 0 && feedItemFlags.length > 0) {
            let nextUpdate;
            if (rateUpdatesMode && !pageNumber) {
              const index = getFirstUnratedIndex(feedItemFlags);
              if (index !== -1) {
                nextUpdate = getActiveUpdateAtIndex({ hits, feedItemFlags, index });
              }
            } else if (!rateUpdatesMode) {
              nextUpdate = getActiveUpdateAtIndex({ hits, feedItemFlags, index: 0 });
            }
            if (nextUpdate && shouldUpdateActiveItem(activeUpdate, nextUpdate)) {
              setActiveUpdate(nextUpdate);
            }
          } else if (shouldUpdateActiveItem(activeUpdate, null)) {
            setActiveUpdate(null);
          }
        }
        const searchParams = new URLSearchParams(location.search);

        const urlToHistory = `${location.pathname}?${searchParams.toString()}${!!hash ? `#${hash}` : ''}`;
        navigate({ search: searchParams.toString(), hash: !!hash ? `#${hash}` : '' }, { replace: true });

        const title = `${mainSectionTitle}${
          sectionName === 'ParliamentaryRecord'
            ? getName({ property: 'Parliamentary record' })
            : sectionName === 'Influence'
              ? location.pathname.startsWith('/campaigns')
                ? 'Campaigns'
                : uppercaseFirstLetter(page.replace('-', ' '))
              : uppercaseFirstLetter(getName({ property: TransformFromPascalCase(sectionName) }))
        } - PolicyMogul`;

        if (!fromInterval) {
          addMetaTags({ title: customTitle ?? title, hash });
        }
        setContentResults((prev) => buildStateResults(prev));

        if (list !== null && list !== 'noListsCreated' && inboxSection) {
          setCurrentlyProcessing({ attempt: currentlyProcessing.attempt + 1, state: results.isReady });
        } else {
          //AE:Added specially for the campaigns page, see if this creates conflicts on different pages
          setCurrentlyProcessing(false);
        }

        setIsLoading(false);

        if (!!list && !pageNumber && list !== 'noListsCreated' && setMarker && inboxSection) {
          await updateMarkers({ contentResults: results });
        }

        if (results.isReady || searchResultsSection) {
          let contentType = changeTypeOfResource(sectionName);
          if (list !== null && list !== 'noListsCreated' && isAuthenticated && notLoadingConditional) {
            await callCollectList(contentType, token, list.id, urlToHistory);
          }
        }
        return buildStateResults(contextResults);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const load_more_results = async (source) => {
    if (!!section) {
      let { thereIsMoreContent, pageNumber } = section;
      if (thereIsMoreContent) {
        return await createResults({ pageNumber: pageNumber + 1, setMarker: false, source });
      }
    }
  };

  return { load_more_results, createResults, isLoading, setIsLoading, currentlyProcessing, setCurrentlyProcessing };
};

export default useCreateResults;
